import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Sparkles, User, Baby, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdmissionsSection = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    childAge: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.parentName || !formData.childAge || !formData.phone) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend
      const response = await axios.post(`${API}/submit-enquiry`, {
        parent_name: formData.parentName,
        child_age: formData.childAge,
        phone: formData.phone,
        email: formData.email || null,
        message: formData.message || null,
      });

      if (response.data.status === 'success') {
        toast.success('Thank you for showing interest in HR KinderNova! Our team will contact you soon for further communication.');
        
        // Reset form
        setFormData({
          parentName: '',
          childAge: '',
          phone: '',
          email: '',
          message: '',
        });
      } else {
        toast.warning(response.data.message || 'Enquiry submitted, but there was an issue with email delivery.');
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again or call us directly at 9467954126.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="admissions" className="py-20" style={{ backgroundColor: 'var(--surface-cream)' }}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold heading-playful mb-4" style={{ color: 'var(--primary-blue)' }}>
              Admissions
            </h2>
            <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: 'var(--primary-yellow)' }}></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: Status & Info */}
            <div className="space-y-6">
              {/* Admissions Open Banner */}
              <Card className="p-8 rounded-3xl border-0 shadow-xl" style={{ backgroundColor: 'var(--primary-yellow)' }}>
                <div className="flex items-center space-x-3 mb-4">
                  <Sparkles className="h-8 w-8" style={{ color: 'var(--primary-red)' }} />
                  <h3 className="text-3xl font-bold heading-playful" style={{ color: 'var(--text-primary)' }}>
                    Admissions Open!
                  </h3>
                </div>
                <p className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Session 2026–27
                </p>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  Enroll your child in our nurturing learning environment today.
                </p>
              </Card>

              {/* Admission Info */}
              <Card className="p-8 rounded-3xl border-0 shadow-lg" style={{ backgroundColor: 'white' }}>
                <h3 className="text-2xl font-bold heading-playful mb-4" style={{ color: 'var(--primary-blue)' }}>
                  Admission Information
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <span className="text-2xl" style={{ color: 'var(--primary-green)' }}>✓</span>
                    <span style={{ color: 'var(--text-secondary)' }}>Classes: Playgroup, Nursery, LKG, UKG, 1st Class</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-2xl" style={{ color: 'var(--primary-green)' }}>✓</span>
                    <span style={{ color: 'var(--text-secondary)' }}>Age-appropriate programs for 2-7 years</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-2xl" style={{ color: 'var(--primary-green)' }}>✓</span>
                    <span style={{ color: 'var(--text-secondary)' }}>Limited seats available</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-2xl" style={{ color: 'var(--primary-green)' }}>✓</span>
                    <span style={{ color: 'var(--text-secondary)' }}>Easy admission process</span>
                  </li>
                </ul>
              </Card>

              {/* Real School Image */}
              <div className="rounded-3xl overflow-hidden shadow-lg h-64">
                <img
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_41ad9bc8-daac-4809-894b-90c60487a24b/artifacts/eei6tlsw_WhatsApp%20Image%202026-07-19%20at%2017.29.58.jpeg"
                  alt="Happy students at HR KinderNova"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right: Enquiry Form */}
            <Card className="p-8 rounded-3xl border-0 shadow-xl" style={{ backgroundColor: 'white' }}>
              <h3 className="text-2xl font-bold heading-playful mb-6" style={{ color: 'var(--primary-red)' }}>
                Submit Your Enquiry
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Parent Name */}
                <div className="space-y-2">
                  <Label htmlFor="parentName" className="flex items-center space-x-2">
                    <User className="h-4 w-4" style={{ color: 'var(--primary-blue)' }} />
                    <span>Parent/Guardian Name *</span>
                  </Label>
                  <Input
                    id="parentName"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="rounded-xl"
                  />
                </div>

                {/* Child Age */}
                <div className="space-y-2">
                  <Label htmlFor="childAge" className="flex items-center space-x-2">
                    <Baby className="h-4 w-4" style={{ color: 'var(--primary-green)' }} />
                    <span>Child's Age *</span>
                  </Label>
                  <Input
                    id="childAge"
                    name="childAge"
                    value={formData.childAge}
                    onChange={handleChange}
                    placeholder="e.g., 3 years or 3.5 years"
                    required
                    className="rounded-xl"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" style={{ color: 'var(--primary-red)' }} />
                    <span>Phone Number *</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    required
                    className="rounded-xl"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" style={{ color: 'var(--primary-orange)' }} />
                    <span>Email (Optional)</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="rounded-xl"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Additional Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Any specific questions or requirements?"
                    rows={4}
                    className="rounded-xl"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full rounded-xl text-lg font-semibold"
                  style={{ backgroundColor: 'var(--primary-blue)', color: 'white' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                </Button>
              </form>

              <p className="text-sm text-center mt-4" style={{ color: 'var(--text-secondary)' }}>
                * Required fields. We'll get back to you within 24 hours.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdmissionsSection;